import { useQuery } from '@tanstack/react-query';
import { API } from '@/utils/API';
import { SectionStatus } from '@/features/submission_forms/types';

export interface SectionProgressData {
  id: string;
  label: string;
  status: SectionStatus;
}

export interface SubmissionProgress {
  percentage_complete: number;
  section_statuses: SectionProgressData[];
}

interface UseSubmissionProgressReturn {
  progress: SubmissionProgress | null;
  isLoading: boolean;
  error: string | null;
  refetch: () => void;
}

/**
 * Hook to fetch backend-calculated submission progress
 * This includes the percentage complete and status of each section
 */
export const useSubmissionProgress = (
  submissionId: string | null | undefined,
  enabled = true
): UseSubmissionProgressReturn => {
  const {
    data: progress = null,
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: ['submission-progress', submissionId],
    queryFn: async (): Promise<SubmissionProgress> => {
      if (!submissionId) {
        throw new Error('Submission ID is required');
      }

      const response = await API.authenticated.fetch(
        `/submissions/${submissionId}/progress`
      );

      if (!response.ok) {
        throw new Error('Failed to fetch submission progress');
      }

      return response.json();
    },
    enabled: enabled && !!submissionId,
    staleTime: 0, // Always fetch fresh data after autosave
    gcTime: 5 * 60 * 1000, // Keep in cache for 5 minutes
  });

  return {
    progress,
    isLoading,
    error: error ? (error as Error).message : null,
    refetch,
  };
};
